import { useEffect, useState } from 'react'

/** What a load can be at any moment. */
export type Async<T> =
  { state: 'loading' } | { state: 'error'; error: Error } | { state: 'ready'; data: T }

/**
 * useApi loads once per changing key and reports all three states.
 *
 * # A failed read says so, and says which request
 *
 * Constitution IV: degradation is never a blocker, and a blank page is the
 * worst kind of degradation because it is indistinguishable from "nothing
 * happened yet". Every failure here renders a message carrying the API's
 * request id, so what a member reports can be found in the log.
 */
export function useApi<T>(
  /**
   * `null` means "not enough is known to ask yet", and the hook stays in
   * `loading` without issuing a request.
   *
   * **This is not the same as a loader that would fail.** A player's page is
   * routed by slug and asks the API by public id, so until the roster resolves
   * the slug there is no id to ask for — and firing anyway requested
   * `/api/v1/players/` with an empty segment, which the fixture server caught
   * as a route nobody covers. Passing an empty string "for now" is how a view
   * ends up making a request it knows is wrong.
   */
  load: ((signal: AbortSignal) => Promise<T>) | null,
  deps: unknown[],
): Async<T> {
  const key = JSON.stringify(deps)
  // The key is stored ALONGSIDE the result rather than reset in the effect.
  // Setting state from inside an effect body renders twice and, on a changing
  // key, briefly shows the previous load's data as if it were the new one.
  // Comparing during render makes "the key moved" mean "loading" with no extra
  // render and no stale frame.
  const [held, setHeld] = useState<{ key: string; result: Async<T> }>({
    key,
    result: { state: 'loading' },
  })

  useEffect(() => {
    if (!load) return
    const controller = new AbortController()
    load(controller.signal)
      .then((data) => setHeld({ key, result: { state: 'ready', data } }))
      .catch((err: unknown) => {
        // An abort is this component unmounting, not a failure to report.
        if (err instanceof DOMException && err.name === 'AbortError') return
        setHeld({
          key,
          result: { state: 'error', error: err instanceof Error ? err : new Error(String(err)) },
        })
      })
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  if (!load) return { state: 'loading' }
  return held.key === key ? held.result : { state: 'loading' }
}
